import {
  LAYOUT_ELEMENT_KINDS,
  type LayoutDraft,
  type LayoutElementKind,
  type PlacedSpot,
} from '@kratispot/shared';

// What the brush lays down: a spot, a zone, an element, or nothing. §18
export const LAYOUT_TOOLS = ['spot', 'zone', ...LAYOUT_ELEMENT_KINDS, 'erase'] as const;

export type LayoutTool = (typeof LAYOUT_TOOLS)[number];

// A spot is drawn before it is priced, so it stands unzoned until Save. §18
export type DraftSpot = Omit<PlacedSpot, 'zoneKey'> & { readonly zoneKey: string | null };

export type EditorDraft = Omit<LayoutDraft, 'spots'> & { readonly spots: readonly DraftSpot[] };

export type Cell = { kind: 'spot'; spot: DraftSpot } | { kind: LayoutElementKind } | null;

const at = (row: number, col: number) => (cell: { gridRow: number; gridCol: number }) =>
  cell.gridRow === row && cell.gridCol === col;

export function cellAt(draft: EditorDraft, row: number, col: number): Cell {
  const spot = draft.spots.find(at(row, col));
  if (spot !== undefined) return { kind: 'spot', spot };
  const element = draft.elements.find(at(row, col));
  return element === undefined ? null : { kind: element.kind };
}

// A painted cell is cleared first, so no two things ever share one. §18
function cleared(draft: EditorDraft, row: number, col: number): EditorDraft {
  return {
    ...draft,
    spots: draft.spots.filter((spot) => !at(row, col)(spot)),
    elements: draft.elements.filter((element) => !at(row, col)(element)),
  };
}

export type Painted = { readonly draft: EditorDraft; readonly used: boolean };

// Re-zoning keeps the number; only a brand new spot spends the counter. §18
export function paint(
  draft: EditorDraft,
  row: number,
  col: number,
  tool: LayoutTool,
  nextNumber: number,
  zoneKey: string,
): Painted {
  const standing = cellAt(draft, row, col);
  if (tool === 'erase') return { draft: cleared(draft, row, col), used: false };
  // The zone brush paints zones onto spots, and touches nothing else. §18
  if (tool === 'zone') {
    if (standing?.kind !== 'spot') return { draft, used: false };
    return {
      draft: {
        ...draft,
        spots: draft.spots.map((spot) => (at(row, col)(spot) ? { ...spot, zoneKey } : spot)),
      },
      used: false,
    };
  }
  if (tool !== 'spot') {
    const base = cleared(draft, row, col);
    return {
      draft: { ...base, elements: [...base.elements, { gridRow: row, gridCol: col, kind: tool }] },
      used: false,
    };
  }
  // Sweeping over a spot already drawn leaves it exactly as it stands. §18
  if (standing?.kind === 'spot') return { draft, used: false };
  const base = cleared(draft, row, col);
  const spot: DraftSpot = { gridRow: row, gridCol: col, number: nextNumber, zoneKey: null };
  return { draft: { ...base, spots: [...base.spots, spot] }, used: true };
}

// Shrinking the canvas drops what falls outside it, rather than hiding it. §18
export function resized(draft: EditorDraft, gridRows: number, gridCols: number): EditorDraft {
  const inside = (cell: { gridRow: number; gridCol: number }) =>
    cell.gridRow <= gridRows && cell.gridCol <= gridCols;
  return {
    ...draft,
    gridRows,
    gridCols,
    spots: draft.spots.filter(inside),
    elements: draft.elements.filter(inside),
  };
}

// §9: a spot with no zone cannot be priced; its number is what is named.
export function unzonedSpots(draft: EditorDraft): number[] {
  return draft.spots.filter((spot) => spot.zoneKey === null).map((spot) => spot.number);
}

// The draft as the API takes it, or null while a spot is still unpriced. §18
export function saveableDraft(draft: EditorDraft): LayoutDraft | null {
  if (unzonedSpots(draft).length > 0) return null;
  return {
    ...draft,
    spots: draft.spots.map((spot) => ({ ...spot, zoneKey: spot.zoneKey as string })),
  };
}
